import { useEffect, useState, useCallback, useRef } from 'react';
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { utcDayRange } from '../photoUpload/dayBuckets';

// Live coverage doc — a single small map of {days, totalPhotos, totalSizeBytes}
// maintained by the server-side storage trigger. Cheap to subscribe to.
export function useProjectCoverage(projectId) {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(!!projectId);
  // The meta/coverage rule requires project ownership, which the backend can
  // only evaluate once the caller's auth token is live. Subscribing before
  // auth settles yields a permission-denied that onSnapshot treats as fatal —
  // it tears the listener down for good, so a transient cold-load race would
  // otherwise blank the coverage strip until a remount. Gate on the auth uid
  // and re-key the effect on it so the listener re-attaches once auth arrives.
  //
  // Track `ready` alongside the uid: a null uid alone is ambiguous — it means
  // "auth still settling" before the first onAuthStateChanged callback, but
  // "genuinely signed out" after it. We only hold `loading` for the former; a
  // resolved-but-signed-out state falls through to loading:false so owner-gated
  // UIs (e.g. OverviewTab's empty-state CTA, which renders nothing while
  // coverage loads) surface instead of hanging on a blank strip forever.
  const [authState, setAuthState] = useState(() =>
    auth.currentUser
      ? { uid: auth.currentUser.uid, ready: true }
      : { uid: null, ready: false });
  const { uid: authUid, ready: authReady } = authState;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) =>
      setAuthState({ uid: user?.uid ?? null, ready: true }));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!projectId) {
      setCoverage(null);
      setLoading(false);
      return undefined;
    }
    if (!authReady) {
      // Auth hasn't resolved yet — wait before reading the owner-gated doc.
      setCoverage(null);
      setLoading(true);
      return undefined;
    }
    if (!authUid) {
      // Auth resolved with no signed-in user: the owner-gated doc is
      // unreadable, so surface an empty (not perpetually-loading) state.
      setCoverage(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ref = doc(db, 'projects', projectId, 'meta', 'coverage');
    const unsub = onSnapshot(ref, (snap) => {
      setCoverage(snap.exists() ? snap.data() : null);
      setLoading(false);
    }, (err) => {
      console.error('useProjectCoverage: read error', err);
      setCoverage(null);
      setLoading(false);
    });
    return () => unsub();
  }, [projectId, authUid, authReady]);

  return { coverage, loading };
}

// Lazy per-day photo loader. NOT an unbounded listener — a getDocs on
// demand when a day is expanded. This is a deliberate design choice: a
// project may have 10,000 photos and paying to keep them all in memory is
// wasted work when the user is only looking at one day at a time.
//
// The 'unknown' bucket queries `where('capturedAt', '==', null)`. Firestore
// permits `==` against null via the JS SDK — this is checked in the
// integration setup. If a project has no 'unknown' entries the query is
// simply empty.
const DAY_PHOTO_CAP = 500;

function buildDayQuery(colRef, dayKey) {
  if (dayKey === 'unknown') {
    return query(
      colRef,
      where('capturedAt', '==', null),
      orderBy('fileName', 'asc'),
      limit(DAY_PHOTO_CAP),
    );
  }
  const range = utcDayRange(dayKey);
  if (!range) return null;
  return query(
    colRef,
    where('capturedAt', '>=', Timestamp.fromMillis(range.startMs)),
    where('capturedAt', '<', Timestamp.fromMillis(range.endMs)),
    orderBy('capturedAt', 'asc'),
    limit(DAY_PHOTO_CAP),
  );
}

export function useDayPhotos(projectId, dayKey, { enabled = true } = {}) {
  const [photos, setPhotos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled || !projectId || !dayKey) return;
    setLoading(true);
    setError(null);
    const requestId = ++requestIdRef.current;
    try {
      const colRef = collection(db, 'projects', projectId, 'photos');
      const q = buildDayQuery(colRef, dayKey);
      if (!q) throw new Error(`useDayPhotos: unknown day key ${dayKey}`);
      const snap = await getDocs(q);
      if (requestId !== requestIdRef.current) return; // A newer request won.
      const rows = [];
      snap.forEach((docSnap) => rows.push({ id: docSnap.id, ...docSnap.data() }));
      setPhotos(rows);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('useDayPhotos: query failed', err);
      setError(err);
      setPhotos([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [projectId, dayKey, enabled]);

  // Clear stale results the instant the target changes, so the grid doesn't
  // flash the previous project/day's photos while the new query is in flight.
  useEffect(() => {
    setPhotos(null);
    setError(null);
  }, [projectId, dayKey]);

  useEffect(() => {
    load();
  }, [load]);

  return { photos, loading, error, reload: load };
}
